import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { Bell, ArrowRight, CheckCircle } from 'lucide-react';
import './Orders.css'; // Let's use some custom css for kanban styling
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiClient';

// Connect to backend websocket
// const socket = io(API_BASE_URL);
const socket = io("https://admin.lingbari.com", {
  withCredentials: true,
});

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const audioRef = useRef(null);

  const fetchOrders = async () => {
    try {
      const response = await apiFetch('/api/orders');
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (e) {
      console.error('Error fetching orders');
    }
  };

  useEffect(() => {
    fetchOrders();

    // Listen for WebSocket events from Backend
    socket.on('new_order', (newOrder) => {
      // Play Audio Notification
      if (audioRef.current) {
        audioRef.current.play().catch(e => console.log('Audio blocked by browser, interaction required'));
      }
      
      // Update State
      setOrders((prev) => [...prev, newOrder]);
    });

    socket.on('order_status_updated', ({ id, status }) => {
      setOrders((prev) => 
        prev.map(ord => ord.id === id ? { ...ord, status } : ord)
      );
    });

    return () => {
      socket.off('new_order');
      socket.off('order_status_updated');
    };
  }, []);

  const changeStatus = async (id, newStatus) => {
    try {
      const response = await apiFetch(`/api/orders/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        // Technically the socket listener will catch it, but doing it optimistically makes UI snappier
        setOrders((prev) => prev.map(o => o.id === id ? { ...o, status: newStatus } : o));
      }
    } catch (e) {
      console.error('Error updating status');
    }
  };

  const getOrdersByStatus = (status) => orders.filter(o => o.status === status);

  // Kanban Columns Data
  const columns = [
    { key: 'received', title: 'Received', color: 'var(--primary-color)', next: 'preparing' },
    { key: 'preparing', title: 'Preparing', color: 'var(--warning-color)', next: 'ready' },
    { key: 'ready', title: 'Ready', color: 'var(--success-color)', next: 'out for delivery' },
    { key: 'out for delivery', title: 'Out for Delivery', color: '#8b5cf6', next: 'delivered' } // 'delivered' will hide it from the board
  ];

  return (
    <div className="orders-page" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      
      <audio ref={audioRef} src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" preload="auto"></audio>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700' }}>Live Orders Kanban</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)' }}>
          <div className="pulse-indicator"></div> Live Sync Active
        </div>
      </div>

      <div className="kanban-board">
        {columns.map(col => (
          <div key={col.key} className="kanban-column" style={{ borderTop: `4px solid ${col.color}` }}>
            <div className="kanban-header">
              <h3>{col.title}</h3>
              <span className="badge">{getOrdersByStatus(col.key).length}</span>
            </div>
            
            <div className="kanban-cards">
              {getOrdersByStatus(col.key).map(order => (
                <div key={order.id} className="kanban-card">
                  <div className="order-top">
                    <span className="order-id">#{order.id}</span>
                    <span className="order-type">{order.order_type}</span>
                  </div>
                  
                  <h4>{order.customer_name}</h4>
                  <p className="order-address">{order.address || 'Takeaway/Dine-in'}</p>
                  
                  <div className="order-bottom">
                    <span className="order-total">${order.total_amount}</span>
                    
                    <button 
                      className="btn move-btn" 
                      onClick={() => changeStatus(order.id, col.next)}
                      title={`Move to ${col.next}`}
                    >
                      {col.next === 'delivered' ? <CheckCircle size={16} /> : <ArrowRight size={16} />}
                    </button>
                  </div>
                </div>
              ))}
              
              {getOrdersByStatus(col.key).length === 0 && (
                <div className="empty-state">No Orders</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Orders;
